import { groupBy, prop } from 'ramda'
import scrollIntoView from 'scroll-into-view-if-needed'

import {
    Component,
    EventEmitter,
    forwardRef,
    Inject,
    Input,
    OnInit,
    Optional,
    Output,
    PLATFORM_ID,
    ViewChild,
} from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { isPlatformBrowser } from '@angular/common'
import {
    ClusterIconStyle,
    GoogleMap,
    MapInfoWindow,
    MapMarker,
} from '@angular/google-maps'
import PlaceResult = google.maps.places.PlaceResult

import { LinkTargetEnum, StrapiMarkerFragment } from '@app-graphql/schema'
import { googleMapStyle, markerClustererStyles } from '@app-domains/google-maps/types/maps-styles'
import { Brands, LocationMarker, Marker } from '@app-domains/google-maps/types/marker'
import { Button } from '@app-domains/ui/components/button/button.component.types'
import { AlgoliaInstantsearchComponent } from '@app-domains/algolia/components/algolia-instantsearch.component'
import { GeolocationService } from '@app-services'
import { coordinatesOfTheNetherlands } from '@app-domains/google-maps/types/locations'

@Component({
    selector: 'app-google-maps',
    templateUrl: './google-maps.component.html',
    styleUrls: ['./google-maps.component.scss'],
})
export class GoogleMapsComponent implements OnInit {

    public locationState: Button.State = 'idle'
    private searchRangeInMeters: number = 50000

    @ViewChild(GoogleMap, { static: false })
    public map: GoogleMap

    public searchForm = new FormGroup({
        searchControl: new FormControl('', {
            nonNullable: true,
        }),
    })

    @Input()
    public options: google.maps.MapOptions = {
            disableDefaultUI: false,
            center: coordinatesOfTheNetherlands,
            zoom: 12,
            zoomControl: true,
            styles: googleMapStyle,
            gestureHandling: 'cooperative',
            maxZoom: 12,
            minZoom: 3,
        }

    @Input()
    public set locationMarkers(markers: Marker[]) {
        this.internalLocationMarkers = markers
        this.groupedMarkers = groupBy(prop('icon'), this.locationMarkers)
    }

    public get locationMarkers(): Marker[] {
        return this.internalLocationMarkers
    }

    public internalLocationMarkers: Marker[] = []

    @Input()
    public cmsMarkers?: StrapiMarkerFragment[]

    @Input()
    public showFilter: boolean = false

    @Input()
    public filterCount: number

    @Output()
    public filterClick = new EventEmitter<void>()

    public selectedMarker?: Marker

    public showMap: boolean

    private currentOpenInfoWindow: MapInfoWindow

    public userMarker: LocationMarker

    public groupedMarkers: Record<Record<'icon', Marker['icon']>['icon'], Record<'icon', Marker['icon']>[] | undefined>

    protected readonly LinkTargetEnum = LinkTargetEnum

    public trackByFn = (marker: any) => marker.objectID

    constructor(
        @Inject(PLATFORM_ID) private platformId: string,
        @Inject(forwardRef(() => AlgoliaInstantsearchComponent))
        @Optional()
        public instantSearchInstance: AlgoliaInstantsearchComponent,
        private geolocation: GeolocationService,
    ) {
        this.showMap = isPlatformBrowser(this.platformId)
    }

    public ngOnInit(): void {
        void this.attemptToGetGeoLocation()

        if (this.cmsMarkers) {
            this.internalLocationMarkers = this.convertCmsMarkers()
            return
        }

        setTimeout(() => {
            this.boundsChanged()
        }, 1000)
    }

    private async attemptToGetGeoLocation(): Promise<void> {
        try {
            const position = await this.geolocation.getLocation()

            if (position) {
                this.options.center = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                }
            }
        } catch (e) {
            // Geolocation not available.
        }
    }

    public handleAddressChange(address: PlaceResult): void {
        const lat = address?.geometry?.location?.lat()
        const lng = address?.geometry?.location?.lng()

        if (! lat || ! lng) {
            return
        }

        this.userMarker = {
            position: { lat, lng },
        }

        this.map.googleMap?.setZoom(11)
        this.map.googleMap?.setCenter({ lat, lng })
    }

    public boundsChanged(): void {
        if (this.cmsMarkers) {
            return
        }

        const center = this.map.getCenter()

        if (center) {
            // Render the markers a bit later than where the map is centered to the new location
            setTimeout(() => {
                this.refetchLocations(center.lat(), center.lng())
            }, 100)
        }
    }

    private refetchLocations(lat: number, lng: number): void {
        const helper = this.instantSearchInstance.instantSearchInstance.helper!

        helper.setQueryParameter('aroundLatLngViaIP', true)
        helper.setQueryParameter('aroundLatLng', `${lat},${lng}`)
        helper.setQueryParameter('aroundRadius', this.searchRangeInMeters)
        helper.setQueryParameter('query', '')

        const boundsData = this.map.getBounds()

        if (boundsData) {
            helper.setQueryParameter(
                'insideBoundingBox',
                boundsData.getSouthWest().lat() + ', ' +
                boundsData.getSouthWest().lng() + ', ' +
                boundsData.getNorthEast().lat() + ', ' +
                boundsData.getNorthEast().lng(),
            )
        }

        helper.search()
    }

    private convertCmsMarkers(): Marker[] {
        return this.cmsMarkers!.map((x) => {
            return {
                objectID: '1',
                lat: x.lat || 0,
                lng: x.lng || 0,
                name: x.address!.name,
                website: x.website || '',
                street: `${x.address!.street} ${x.address!.number}`,
                postalCode: x.address!.zipcode,
                city: x.address!.city,
                icon: 'dealer',
                brands: [Brands.woood, Brands.bepurehome, Brands.vtwonen],
                country: x.address!.country,
                country_code: undefined,
            }
        })
    }

    public markerClick(mapMarker: MapMarker, marker: Marker, infoWindow: MapInfoWindow): void {
        if (this.currentOpenInfoWindow) {
            this.currentOpenInfoWindow.close()
        }

        this.currentOpenInfoWindow = infoWindow
        this.currentOpenInfoWindow.open(mapMarker)
        this.selectedMarker = marker

        const el = document.getElementById(marker.objectID)

        if (el) {
            let scroll = true

            scrollIntoView(el, {
                scrollMode: 'if-needed',
                block: 'start',
                boundary: (p: Element) => {
                    if (p.classList.contains('filter-container')) {
                        scroll = false
                    }
                    return scroll
                },
            })
        }
    }

    public setFocus(marker: Marker): void {
        if (this.currentOpenInfoWindow) {
            this.currentOpenInfoWindow.close()
        }

        const position = new google.maps.LatLng(marker.lat, marker.lng)
        this.map.googleMap?.setCenter(position)
        this.map.googleMap?.setZoom(12)
        this.selectedMarker = marker
    }

    public openFilterDialog(): void {
        this.filterClick.emit()
    }

    public async currentLocation(): Promise<void> {
        this.locationState = 'loading'

        try {
            const position = await this.geolocation.getLocation()

            if (position) {
                this.locationState = 'idle'
                this.options.center = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                }

                this.options.zoom = 14

                this.map.panTo({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                })

                this.map.zoomChanged.subscribe()

                this.userMarker = {
                    position: {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    },
                }
            }
        } catch (error) {
            // Geolocation not available.
        }
    }

    public getMarkerClustererStyles(markers: Marker[]): ClusterIconStyle[] {
        if (! markers.length) {
            return markerClustererStyles
        }

        const markerIcon = markers[0].icon ?? 'dealer'

        return [
            {
                ...markerClustererStyles[0],
                url: `/assets/images/map-markers/${markerIcon}-cluster.svg`,
            },
        ]
    }
}
